import { default as Modal } from 'antd/es/modal';
import { CryptoFlag } from 'shared/src/CryptoFlag';
import { CryptoNetworkIcon } from 'shared/src/CryptoNetworkIcon';

import { hasValue } from 'utils/hasValue';

import type { GetCryptoDeposits200DataItem } from 'api/customerApi';

import { CryptoAddress } from '@atoms/CryptoAddress/CryptoAddress';

import { FormattedDateTime } from '@molecules/FormattedDateTime';

import { depositStatusColor } from './DepositsTable';

import { getNetworkDisplayName } from '../config';
import { StatusBadge } from '../shared/StatusBadge';

export const DepositDetailModal = ({
  deposit,
  onClose
}: {
  deposit: GetCryptoDeposits200DataItem;
  onClose: () => void;
}) => {
  return (
    <Modal
      title={null}
      open={!!deposit}
      onCancel={onClose}
      footer={null}
      width={400}
    >
      <div className="flex flex-col items-center gap-4">
        <p className="text-xl text-content-primary">Received {deposit.asset}</p>
        <div className="relative inline-flex">
          <CryptoFlag cryptoCode={deposit.asset} className="size-8" />
          <CryptoNetworkIcon
            network={deposit.network ?? ''}
            className="absolute -bottom-0.5 -right-0.5 size-4"
          />
        </div>
        <FormattedDateTime value={deposit.layer1_created_at} />
      </div>

      <div className="flex flex-col gap-4 px-2 pb-4">
        <p className="text-base font-bold text-content-primary">
          Transaction details
        </p>

        <div className="flex flex-row justify-between gap-1">
          <span className="font-semibold text-content-secondary">Amount</span>
          <span className="text-content-primary">
            {deposit.formatted_amount
              ? `${deposit.formatted_amount} ${deposit.asset}`
              : deposit.amount}
          </span>
        </div>

        {hasValue(deposit.from_address) && (
          <div className="flex flex-row justify-between gap-1">
            <span className="font-semibold text-content-secondary">
              From address
            </span>
            <CryptoAddress address={deposit.from_address} />
          </div>
        )}

        {hasValue(deposit.deposit_address) && (
          <div className="flex flex-row justify-between gap-1">
            <span className="font-semibold text-content-secondary">
              To address
            </span>
            <CryptoAddress address={deposit.deposit_address} />
          </div>
        )}

        {hasValue(deposit.tx_hash) && (
          <div className="flex flex-row justify-between gap-1">
            <span className="font-semibold text-content-secondary">
              Transaction hash
            </span>
            <CryptoAddress address={deposit.tx_hash} />
          </div>
        )}

        {hasValue(deposit.network) && (
          <div className="flex flex-row justify-between gap-1">
            <span className="font-semibold text-content-secondary">
              Network
            </span>
            <span className="inline-flex items-center gap-2 text-sm text-content-primary">
              <CryptoNetworkIcon network={deposit.network} className="size-5" />
              {getNetworkDisplayName(deposit.network)}
            </span>
          </div>
        )}

        <div className="flex flex-row justify-between gap-1">
          <span className="font-semibold text-content-secondary">Status</span>
          <div>
            <StatusBadge
              statusText={deposit.status}
              color={depositStatusColor[deposit.status]}
            />
          </div>
        </div>
      </div>
    </Modal>
  );
};
